import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertCircle, X } from "lucide-react";
import { parseUploadedFile, downloadSampleTemplate } from "@/lib/csv";
import { REQUIRED_FIELDS, OPTIONAL_FIELDS, NUMERIC_FIELDS } from "@/lib/forecast";
import { useApp } from "@/contexts/AppContext";

const STEP_LABELS = ["Upload", "Preview", "Map", "Validate", "Run"];

function autoMap(headers) {
  const all = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
  const norm = (s) => String(s).toLowerCase().replace(/[\s_\-]/g, "");
  const headerNorm = headers.map((h) => ({ raw: h, n: norm(h) }));
  const result = {};
  all.forEach((field) => {
    const match = headerNorm.find((h) => h.n === norm(field));
    if (match) result[field] = match.raw;
  });
  return result;
}

export default function ImportData() {
  const navigate = useNavigate();
  const { setRows, setFileName } = useApp();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [pendingName, setPendingName] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls") && !name.endsWith(".csv")) {
      toast.error("Unsupported file format. Please upload .xlsx, .xls, or .csv");
      return;
    }
    try {
      const { rows, headers } = await parseUploadedFile(file);
      if (!rows.length) {
        toast.error("The file appears to be empty.");
        return;
      }
      setParsedRows(rows);
      setHeaders(headers);
      setMapping(autoMap(headers));
      setPendingName(file.name);
      setStep(1);
      toast.success(`Loaded ${rows.length} rows from ${file.name}`);
    } catch (err) {
      toast.error("Unable to parse file: " + err.message);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const validate = () => {
    const errors = [];
    const missing = REQUIRED_FIELDS.filter((f) => !mapping[f]);
    if (missing.length) {
      errors.push({ type: "missing_mapping", message: `Required fields not mapped: ${missing.join(", ")}` });
      return errors;
    }
    parsedRows.forEach((row, i) => {
      REQUIRED_FIELDS.forEach((f) => {
        const src = mapping[f];
        const v = row[src];
        if (v === "" || v === null || v === undefined) {
          errors.push({ type: "empty", message: `Row ${i + 2}: ${f} is empty` });
        } else if (NUMERIC_FIELDS.has(f) && isNaN(Number(v))) {
          errors.push({ type: "not_number", message: `Row ${i + 2}: ${f} (\"${v}\") must be numeric` });
        }
      });
    });
    return errors.slice(0, 50);
  };

  const runForecast = () => {
    const errors = validate();
    setValidationErrors(errors);
    if (errors.length) {
      setStep(3);
      toast.error(`Found ${errors.length} validation issue${errors.length === 1 ? "" : "s"}. Review mapping.`);
      return;
    }
    const mapped = parsedRows.map((row) => {
      const out = {};
      [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].forEach((f) => {
        if (mapping[f]) {
          const v = row[mapping[f]];
          out[f] = NUMERIC_FIELDS.has(f) && v !== "" ? Number(v) : v;
        }
      });
      return out;
    });
    setRows(mapped);
    setFileName(pendingName);
    toast.success("Forecast computed. Redirecting to dashboard…");
    setTimeout(() => navigate("/"), 500);
  };

  return (
    <>
      <Topbar
        title="Import MRO Data"
        subtitle="Upload your Excel or CSV to run the forecast engine on your data."
        actions={
          <Button
            data-testid="download-template-btn"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => { downloadSampleTemplate(); toast.success("Sample template downloaded."); }}
          >
            <Download className="h-4 w-4" /> Sample Template
          </Button>
        }
      />

      <div className="flex-1 px-8 py-7 space-y-6">
        {/* Stepper */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex items-center gap-2 shrink-0">
                <div data-testid={`step-${i}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  i === step
                    ? "bg-slate-900 text-white border-slate-900"
                    : i < step
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-50 text-slate-500 border-slate-200"
                }`}>
                  <span className="font-mono">{String(i + 1).padStart(2, "0")}</span>
                  <span>{label}</span>
                </div>
                {i < STEP_LABELS.length - 1 && <div className="h-px w-8 bg-slate-200" />}
              </div>
            ))}
          </div>
        </div>

        {step === 0 && (
          <div
            data-testid="upload-dropzone"
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            className={`bg-white border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive ? "border-slate-900 bg-slate-50" : "border-slate-300"
            }`}
          >
            <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5">
              <UploadCloud className="h-6 w-6 text-slate-600" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-slate-900">
              Upload your MRO consumables file
            </h3>
            <p className="text-sm text-slate-500 mt-2">
              Drag and drop, or browse. We support <span className="font-mono">.xlsx</span>, <span className="font-mono">.xls</span>, and <span className="font-mono">.csv</span>.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <input
                ref={fileInputRef}
                data-testid="file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Button
                data-testid="browse-file-btn"
                onClick={() => fileInputRef.current?.click()}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Browse Files
              </Button>
              <Button
                data-testid="download-template-btn-inline"
                variant="outline"
                onClick={() => { downloadSampleTemplate(); toast.success("Sample template downloaded."); }}
              >
                <Download className="h-4 w-4 mr-1.5" /> Sample Template
              </Button>
            </div>
          </div>
        )}

        {step > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="font-mono text-sm text-slate-900 truncate">{pendingName}</span>
                <span className="text-xs text-slate-500">· {parsedRows.length} rows · {headers.length} cols</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setStep(0); setParsedRows([]); setHeaders([]); setMapping({}); }} data-testid="clear-upload-btn">
                <X className="h-4 w-4 mr-1" /> Change file
              </Button>
            </div>

            {step === 1 && (
              <div className="p-5 space-y-4">
                <h3 className="font-heading font-semibold text-slate-900">Preview (first 10 rows)</h3>
                <div className="overflow-x-auto border border-slate-200 rounded-md">
                  <table className="data-table" data-testid="preview-table">
                    <thead>
                      <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0, 10).map((r, i) => (
                        <tr key={i}>
                          {headers.map((h) => <td key={h}>{String(r[h] ?? "")}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end">
                  <Button data-testid="to-map-btn" onClick={() => setStep(2)} className="bg-slate-900 hover:bg-slate-800 text-white">
                    Continue to Mapping
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="p-5 space-y-5">
                <div>
                  <h3 className="font-heading font-semibold text-slate-900">Map Columns</h3>
                  <p className="text-xs text-slate-500 mt-1">Match your file columns to Avyntis fields. Auto-mapped fields are already selected.</p>
                </div>
                <MappingSection title="Required Fields" fields={REQUIRED_FIELDS} headers={headers} mapping={mapping} setMapping={setMapping} required testid="required-mapping" />
                <MappingSection title="Optional Fields" fields={OPTIONAL_FIELDS} headers={headers} mapping={mapping} setMapping={setMapping} testid="optional-mapping" />
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button data-testid="run-forecast-btn" onClick={runForecast} className="bg-slate-900 hover:bg-slate-800 text-white">
                    Run Forecast Analysis
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  <h3 className="font-heading font-semibold">Validation Issues</h3>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-md p-4 max-h-80 overflow-auto" data-testid="validation-errors">
                  <ul className="text-sm text-red-800 space-y-1 font-mono">
                    {validationErrors.map((e, i) => <li key={`err-${i}-${e.type}`}>• {e.message}</li>)}
                  </ul>
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>Back to Mapping</Button>
                  <Button data-testid="retry-forecast-btn" onClick={runForecast} className="bg-slate-900 hover:bg-slate-800 text-white">
                    Re-validate & Run
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function MappingSection({ title, fields, headers, mapping, setMapping, required = false, testid }) {
  return (
    <div data-testid={testid}>
      <h4 className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-3">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map((f) => {
          const mapped = mapping[f];
          return (
            <div key={f} className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[12px] font-semibold text-slate-900">{f}</label>
                {required && !mapped && <span className="text-[10px] text-red-600">Required</span>}
                {mapped && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              </div>
              <Select
                value={mapped || "__none__"}
                onValueChange={(v) => {
                  const next = { ...mapping };
                  if (v === "__none__") delete next[f];
                  else next[f] = v;
                  setMapping(next);
                }}
              >
                <SelectTrigger data-testid={`map-${f}`} className="h-9 bg-white text-xs">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— not mapped —</SelectItem>
                  {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
